import React, { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Box, Typography, Button, Alert, Snackbar, Fab } from '@mui/material';
import { GetApp as InstallIcon, WifiOff as OfflineIcon, Close as CloseIcon } from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

// Import theme system and providers
import { ThemeProvider } from './contexts/ThemeContext';
import { AuthProvider } from './contexts/AuthContext';
import { SnackbarProvider } from './contexts/SnackbarContext';
import { TeamManagementProvider } from './contexts/TeamManagementContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { FinancialEngineProvider } from './contexts/FinancialEngineContext';
import { ResourceOptimizationProvider } from './contexts/ResourceOptimizationContext';
import { DashboardScopeProvider } from './contexts/DashboardScopeContext';
import DashboardScopeStrip from './components/dashboard/shared/DashboardScopeStrip';
import { isDashboardScopeRoute } from './utils/dashboardScope';
import { ToastProvider, LoadingOverlayComponent, ProgressBar } from './components/shared/LoadingSystem';

// Import Pulse branding styles
import './styles/pulseBranding.css';

// Import accessibility system
import { AccessibilityProvider } from './components/accessibility/AccessibilityProvider';
import AccessibilityControls from './components/accessibility/AccessibilityControls';
import './components/accessibility/accessibility.css';

// Import enhanced components
import EnhancedNavigation from './components/navigation/EnhancedNavigation';
import PageContextSubtitle from './components/navigation/PageContextSubtitle';
import ErrorBoundary from './components/shared/ErrorBoundary';
import PrivateRoute from './components/PrivateRoute';
import ClientRoute from './components/ClientRoute';
import ExecutiveDashboardRouteGuard from './components/routing/ExecutiveDashboardRouteGuard';
import DeliveryCockpitEditionGuard from './components/routing/DeliveryCockpitEditionGuard';
import DeferredRouteRedirect from './components/routing/DeferredRouteRedirect';
import ProjectOperationsLegacyRedirect from './components/routing/ProjectOperationsLegacyRedirect';
import AppRouteNotFound from './components/routing/AppRouteNotFound';
import { FINANCIAL_DASHBOARD_ALLOWED_ROLES } from './constants/financialAccess';
import { isDeferredRoutePath } from './constants/deferredRoutePaths';
// Temporarily eager load EnhancedFinancialDashboard to fix Tabs chunk loading issue
import EnhancedFinancialDashboard from './components/dashboard/EnhancedFinancialDashboard';

// Import PWA hook
import { usePWA } from './hooks/usePWA';

// Import mobile components
import MobileBottomNavigation from './components/shared/MobileBottomNavigation';

// Delivery cockpit onboarding / help wizard
import { DeliveryCockpitHelpProvider } from './contexts/DeliveryCockpitHelpContext';
import DeliveryCockpitTourCoach from './components/onboarding/DeliveryCockpitTourCoach';
import TourSpotlight from './components/onboarding/TourSpotlight';
const NewUserGuidePage = lazy(() => import('./components/onboarding/NewUserGuidePage'));

// Lazy load components for better performance
const DashboardWrapper = lazy(() => import('./components/dashboard/DashboardWrapper'));
// RefactoredProjects - removed, using ProjectOperationsDashboard instead
const RefactoredTimesheets = lazy(() => import('./components/RefactoredTimesheets'));
const ExpenseDashboard = lazy(() => import('./components/expenses/ExpenseDashboard'));
const HelpDeskDashboard = lazy(() => import('./components/helpdesk/HelpDeskDashboard'));
const HelpCenterPage = lazy(() => import('./components/help/HelpCenterPage'));
const GPSDashboard = lazy(() => import('./components/gps/GPSDashboard'));
// RefactoredCostDashboard - removed, using EnhancedFinancialDashboard instead
const UnifiedPSADashboard = lazy(() => import('./components/dashboard/UnifiedPSADashboard'));
const Reports = lazy(() => import('./components/Reports'));
const TeamManagement = lazy(() => import('./components/TeamManagement'));
const UsersPage = lazy(() => import('./components/users/UsersPage'));
// Admin - removed, using AdminDashboard instead
const AccessibilityDemo = lazy(() => import('./components/accessibility/AccessibilityDemo'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
// TaskList - removed, using MyWorkspace for individual tasks

// Phase 2 Components - Industry Standard Productivity Analytics
const EstimationAccuracyProfile = lazy(() => import('./components/shared/EstimationAccuracyProfile'));
const TaskCompletionRateWidget = lazy(() => import('./components/shared/TaskCompletionRateWidget'));

// Phase 3.1 Legacy Dashboard Cleanup:
// - ResourceOptimizationDashboard → features consolidated into ProjectOperationsDashboard
// - MLDashboard → features consolidated into AnalyticsDashboard
const QuickBooksDashboard = lazy(() => import('./components/accounting/QuickBooksDashboard'));
const XeroDashboard = lazy(() => import('./components/accounting/XeroDashboard'));
const RevenueRecognition = lazy(() => import('./components/accounting/RevenueRecognition'));
const InvoiceManagement = lazy(() => import('./components/accounting/InvoiceManagement'));

// Week 5: Analytics & BI Dashboard Enhancement
const AnalyticsDashboard = lazy(() => import('./components/dashboard/AnalyticsDashboard'));

// AI Intelligence Engine
const AIIntelligenceChat = lazy(() => import('./components/ai/AIIntelligenceChat'));

// CRM Integration Dashboard
const CRMDashboard = lazy(() => import('./components/crm/CRMDashboard'));

// Communication Platforms Dashboard
const CommunicationDashboard = lazy(() => import('./components/communication/CommunicationDashboard'));

// Week 4: Client Portal Foundation
const ClientPortalApp = lazy(() => import('./components/client-portal/ClientPortalApp'));

// Phase 2: Dashboard Consolidation - NEW CONSOLIDATED DASHBOARDS
// EnhancedFinancialDashboard is eagerly loaded above to fix Tabs chunk loading issue
const ProjectOperationsDashboard = lazy(() => import('./components/dashboard/ProjectOperationsDashboard'));
const ProjectManagementPage = lazy(() => import('./components/projects/ProjectManagementPage'));
const DocumentControlPage = lazy(() => import('./components/projects/DocumentControlPage'));
const GanttChartPage = lazy(() => import('./components/projects/GanttChartPage'));
const EnhancedTaskAssignment = lazy(() => import('./components/projects/EnhancedTaskAssignment'));
const PMIntegrationPanel = lazy(() => import('./components/projects/PMIntegrationPanel'));
const ProjectResourceHubTest = lazy(() => import('./components/projects/ProjectResourceHubTest'));
const ProjectResourceHubPage = lazy(() => import('./components/projects/ProjectResourceHubPage'));
const MyWorkspace = lazy(() => import('./components/dashboard/MyWorkspace'));
const OverviewDashboard = lazy(() => import('./components/dashboard/OverviewDashboard'));

// Admin Components
const TenantManagement = lazy(() => import('./components/admin/TenantManagement'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));

// ML Resource Optimization Component
// Removed unused lazy import to satisfy ESLint no-unused-vars

// API Test Component
const ApiTestPage = lazy(() => import('./components/ApiTestPage'));

// Theme Showcase Component
const ThemeShowcase = lazy(() => import('./components/shared/ThemeShowcase'));

const ObservabilityDashboard = lazy(() => import('./components/observability/ObservabilityDashboard'));


// Loading fallback component
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      backgroundColor: 'background.default',
    }}
  >
    <LoadingOverlayComponent open={true} message="Loading application..." />
  </Box>
);

const FinancialDashboardAliasRoute = () => (
  <DeferredRouteRedirect to="/financial-management" />
);

const TaskCompletionRoute = () => {
  const { projectId } = useParams();
  const resolvedProjectId = Number(projectId);

  if (!projectId || Number.isNaN(resolvedProjectId) || resolvedProjectId <= 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Please select a project to view task completion analytics.
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Project identifier is required to load completion rate and trend metrics.
        </Typography>
      </Box>
    );
  }

  return <TaskCompletionRateWidget projectId={resolvedProjectId} />;
};

const ROUTE_CONTEXT_LABELS = [
  { prefix: '/executive-dashboard', title: 'Executive dashboard' },
  { prefix: '/financial-management', title: 'Financial management' },
  { prefix: '/overview-dashboard', title: 'Overview dashboard' },
  { prefix: '/analytics-dashboard', title: 'Analytics' },
  { prefix: '/advanced-analytics', title: 'Advanced analytics' },
  { prefix: '/resource-planning', title: 'Resource planning' },
  { prefix: '/timesheets', title: 'Timesheets' },
  { prefix: '/projects', title: 'Project operations' },
];

const resolvePageContext = (pathname) => {
  if (isDeferredRoutePath(pathname)) {
    return null;
  }
  const match = ROUTE_CONTEXT_LABELS.find(({ prefix }) => pathname.startsWith(prefix));
  return match ? match.title : null;
};

// Main layout component
const MainLayout = ({ children }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const location = useLocation();
  const pageContextTitle = resolvePageContext(location.pathname);
  const notificationCount = 0;

  return (
    <DeliveryCockpitHelpProvider>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <EnhancedNavigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            ml: { md: '221px' },
            mt: '64px',
            mb: { xs: '80px', md: 0 }, // Space for mobile bottom navigation
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
            // Almost no padding for maximum content width
            px: { xs: 0.25, sm: 0.25, md: 0.5 }, // Even more minimal padding
            py: { xs: 1, sm: 1.5 }, // Minimal vertical padding
            overflow: 'auto',
          }}
        >
          {pageContextTitle && (
            <PageContextSubtitle title={pageContextTitle} detail={location.pathname} />
          )}
          {isDashboardScopeRoute(location.pathname) && <DashboardScopeStrip />}
          {children}
        </Box>

        {/* Mobile Bottom Navigation */}
        <MobileBottomNavigation
          onMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
          notificationCount={notificationCount}
        />
      </Box>
      <TourSpotlight />
      <DeliveryCockpitTourCoach />
    </DeliveryCockpitHelpProvider>
  );
};

// Auth layout component
const AuthLayout = ({ children }) => {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.default',
        backgroundImage: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      {children}
    </Box>
  );
};

const ProtectedRoute = ({ children }) => (
  <PrivateRoute>
    <MainLayout>
      {children}
    </MainLayout>
  </PrivateRoute>
);

const PublicAuthRoute = ({ children }) => (
  <AuthLayout>
    {children}
  </AuthLayout>
);

function App() {
  const {
    isInstallable,
    isInstalled,
    isOnline,
    installPWA,
    dismissInstallPrompt
  } = usePWA();

  // Register service worker (production/PWA only — dev + Cypress unregister to avoid stale chunk cache)
  useEffect(() => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    const unregisterServiceWorkers = () => {
      navigator.serviceWorker.getRegistrations?.()
        .then((registrations) => Promise.all(
          registrations.map((registration) => registration.unregister())
        ))
        .catch((error) => {
          console.warn('[PWA] SW unregister failed:', error);
        });
    };

    if (window.Cypress) {
      import('./hooks/useAnalytics')
        .then(({ resetAnalyticsCaches }) => {
          window.__resetAnalyticsCaches = resetAnalyticsCaches;
        })
        .catch((error) => {
          console.warn('[Cypress] Failed to expose resetAnalyticsCaches:', error);
        });
      unregisterServiceWorkers();
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      unregisterServiceWorkers();
      return;
    }

    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('[PWA] SW registered:', registration.scope);
          })
          .catch((error) => {
            console.error('[PWA] SW registration failed:', error);
          });
      });
    }
  }, []);

  return (
    <ErrorBoundary>
      <AccessibilityProvider>
        <ThemeProvider>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <SnackbarProvider>
              <ToastProvider>
                <AuthProvider>
                  <DashboardScopeProvider>
                    <ProjectProvider>
                      <TeamManagementProvider>
                        <FinancialEngineProvider>
                          <ResourceOptimizationProvider>
                            <ProgressBar />
                            <AccessibilityControls />
                            <Suspense fallback={<LoadingFallback />}>
                              <Routes>
                                {/* Public routes */}
                                <Route
                                  path="/login"
                                  element={
                                    <PublicAuthRoute>
                                      <Login />
                                    </PublicAuthRoute>
                                  }
                                />
                                <Route
                                  path="/register"
                                  element={
                                    <PublicAuthRoute>
                                      <Register />
                                    </PublicAuthRoute>
                                  }
                                />
                                <Route path="/getting-started" element={<NewUserGuidePage />} />
                                <Route path="/user-guide" element={<Navigate to="/getting-started" replace />} />

                                {/* Protected routes */}
                                <Route
                                  path="/"
                                  element={
                                    <PrivateRoute>
                                      <Navigate to="/dashboard" replace />
                                    </PrivateRoute>
                                  }
                                />

                                <Route
                                  path="/dashboard"
                                  element={
                                    <ProtectedRoute>
                                      <DashboardWrapper />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Phase 2.2: Project Operations Consolidation - Manager Level */}
                                <Route
                                  path="/projects/*"
                                  element={
                                    <ProtectedRoute>
                                      <ProjectOperationsDashboard />
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/timesheets/*"
                                  element={
                                    <ProtectedRoute>
                                      <ErrorBoundary>
                                        <RefactoredTimesheets />
                                      </ErrorBoundary>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/expenses"
                                  element={
                                    <ProtectedRoute>
                                      <ExpenseDashboard />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/helpdesk"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Helpdesk">
                                        <HelpDeskDashboard />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/gps-field-tracking"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="GPS field tracking">
                                        <GPSDashboard />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Phase 2.3: My Workspace - Individual Contributors */}
                                <Route
                                  path="/tasks/*"
                                  element={
                                    <ProtectedRoute>
                                      <MyWorkspace />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Phase 2.2: Project Operations Dashboard - Manager Level */}
                                <Route
                                  path="/project-operations/:projectId"
                                  element={
                                    <ProtectedRoute>
                                      <ProjectOperationsLegacyRedirect />
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/project-operations"
                                  element={
                                    <ProtectedRoute>
                                      <ProjectOperationsDashboard />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Project Management Page - Industry Standard PSA Interface */}
                                <Route
                                  path="/project-management/new"
                                  element={<Navigate to="/project-management?action=create" replace />}
                                />
                                <Route
                                  path="/project-management/:projectId?"
                                  element={
                                    <ProtectedRoute>
                                      <ProjectManagementPage />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Document Control Page - Project Document Management */}
                                <Route
                                  path="/document-control/:projectId?"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Document control">
                                        <DocumentControlPage />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Gantt Chart Page - Project Scheduling & Timeline Management */}
                                <Route
                                  path="/gantt-chart/:projectId?"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Gantt chart">
                                        <GanttChartPage />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* PM Integration Panel - Enterprise PM Tool Integration */}
                                <Route
                                  path="/pm-integration"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="PM integration">
                                        <PMIntegrationPanel />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Enhanced Task Assignment - Industry Standard PSA Task Assignment */}
                                <Route
                                  path="/project-management/:projectId/task-assignment"
                                  element={
                                    <ProtectedRoute>
                                      <EnhancedTaskAssignment />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Project Resource Hub - Unified Resource Management Interface */}
                                <Route
                                  path="/resource-hub-test"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Resource hub test">
                                        <ProjectResourceHubTest />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />
                                <Route
                                  path="/projects/:projectId/resources"
                                  element={
                                    <ProtectedRoute>
                                      <ProjectResourceHubPage />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Phase 2.3: My Workspace - Individual Contributors */}
                                <Route
                                  path="/my-workspace"
                                  element={
                                    <ProtectedRoute>
                                      <MyWorkspace />
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/help"
                                  element={
                                    <ProtectedRoute>
                                      <HelpCenterPage />
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/profile"
                                  element={<Navigate to="/my-workspace" replace />}
                                />
                                <Route
                                  path="/settings"
                                  element={<Navigate to="/my-workspace" replace />}
                                />

                                {/* Phase 2.1: Financial Dashboard Consolidation - Enhanced Financial Dashboard */}
                                <Route
                                  path="/cost-dashboard"
                                  element={
                                    <ProtectedRoute>
                                      <FinancialDashboardAliasRoute />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Phase 2.1: Enhanced Financial Management (NEW) */}
                                <Route
                                  path="/financial-management"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard
                                        featureName="Financial management"
                                        additionalAllowedRoles={FINANCIAL_DASHBOARD_ALLOWED_ROLES}
                                      >
                                        <EnhancedFinancialDashboard />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Phase 1.3: Financial Dashboard - DEPRECATED - Consolidated into Executive Dashboard */}
                                <Route
                                  path="/financial-dashboard"
                                  element={
                                    <ProtectedRoute>
                                      <DeferredRouteRedirect to="/executive-dashboard" />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Backward Compatibility Redirects for Phase 2 Consolidation */}
                                <Route
                                  path="/cost-dashboard-legacy"
                                  element={
                                    <ProtectedRoute>
                                      <DeferredRouteRedirect to="/financial-management" />
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/projects-legacy"
                                  element={
                                    <ProtectedRoute>
                                      <Navigate to="/project-operations" replace />
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/tasks-legacy"
                                  element={
                                    <ProtectedRoute>
                                      <Navigate to="/project-operations" replace />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Executive Dashboard - Industry-Standard PSA Executive Overview */}
                                <Route
                                  path="/executive-dashboard"
                                  element={
                                    <ProtectedRoute>
                                      <ExecutiveDashboardRouteGuard featureName="Executive dashboard">
                                        <UnifiedPSADashboard />
                                      </ExecutiveDashboardRouteGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Overview Dashboard — executive-tier only; PM bookmarks redirect to wedge */}
                                <Route
                                  path="/overview-dashboard"
                                  element={
                                    <ProtectedRoute>
                                      <ExecutiveDashboardRouteGuard featureName="Overview dashboard">
                                        <OverviewDashboard />
                                      </ExecutiveDashboardRouteGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Week 5: Analytics & BI Dashboard Enhancement */}
                                <Route
                                  path="/analytics-dashboard"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Analytics and business intelligence">
                                        <AnalyticsDashboard />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Advanced Analytics with AI Insights → consolidated under Analytics Dashboard */}
                                <Route
                                  path="/advanced-analytics"
                                  element={
                                    <ProtectedRoute>
                                      <DeferredRouteRedirect to="/analytics-dashboard?view=predictive" />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* AI Intelligence Chat */}
                                <Route
                                  path="/ai-intelligence"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="AI intelligence">
                                        <AIIntelligenceChat />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* CRM Integration Dashboard */}
                                <Route
                                  path="/crm-integration"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="CRM integration">
                                        <CRMDashboard />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Communication Platforms Dashboard */}
                                <Route
                                  path="/communication"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Communication platforms">
                                        <CommunicationDashboard />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/resource-planning"
                                  element={
                                    <ProtectedRoute>
                                      <Navigate to="/project-operations?tab=staffing" replace />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* ML features integrated into Resource Planning Dashboard (industry standard) */}

                                {/* Phase 3: ML Dashboard → REDIRECT to Analytics Dashboard (Phase 3.1 Consolidation) */}
                                <Route
                                  path="/ml-dashboard"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Analytics and business intelligence">
                                        <AnalyticsDashboard />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Phase 3.2: QuickBooks Integration */}
                                <Route
                                  path="/quickbooks"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="QuickBooks integration">
                                        <QuickBooksDashboard />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Phase 3.2: Xero Integration */}
                                <Route
                                  path="/xero"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Xero integration">
                                        <XeroDashboard />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Revenue Recognition System */}
                                <Route
                                  path="/revenue-recognition"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Revenue recognition">
                                        <RevenueRecognition />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Invoice Management System */}
                                <Route
                                  path="/invoice-management"
                                  element={
                                    <ProtectedRoute>
                                      <InvoiceManagement />
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/reports/*"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Reports">
                                        <Reports />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Phase 2: Industry Standard Productivity Analytics → REDIRECT to Analytics Dashboard (Phase 3.1 Consolidation) */}
                                <Route
                                  path="/analytics/team-productivity"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Analytics and business intelligence">
                                        <AnalyticsDashboard />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/analytics/task-completion"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Analytics and business intelligence">
                                        <TaskCompletionRoute />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/analytics/task-completion/:projectId"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Analytics and business intelligence">
                                        <TaskCompletionRoute />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/analytics/estimation-accuracy"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Analytics and business intelligence">
                                        <EstimationAccuracyProfile />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/team/*"
                                  element={
                                    <ProtectedRoute>
                                      <TeamManagement />
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/users/*"
                                  element={
                                    <ProtectedRoute>
                                      <UsersPage />
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/admin"
                                  element={
                                    <ProtectedRoute>
                                      <AdminDashboard />
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/admin/tenants"
                                  element={
                                    <ProtectedRoute>
                                      <TenantManagement />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* API Test Page - For debugging and demonstrating backend endpoints */}
                                <Route
                                  path="/api-test"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="API test console">
                                        <ApiTestPage />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                <Route
                                  path="/accessibility-demo"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Accessibility demo">
                                        <AccessibilityDemo />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Theme Showcase - Pulse Branding Demo */}
                                <Route
                                  path="/theme-showcase"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Theme showcase">
                                        <ThemeShowcase />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Observability Dashboard */}
                                <Route
                                  path="/observability"
                                  element={
                                    <ProtectedRoute>
                                      <DeliveryCockpitEditionGuard featureName="Observability">
                                        <ObservabilityDashboard />
                                      </DeliveryCockpitEditionGuard>
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Phase 2.4: Client Portal Integration - Existing Code Leveraged */}
                                <Route
                                  path="/client-portal/*"
                                  element={
                                    <ClientRoute>
                                      <ClientPortalApp />
                                    </ClientRoute>
                                  }
                                />

                                {/* Timesheet Adjustment Route */}
                                <Route
                                  path="/:tenantId/adjust"
                                  element={
                                    <ProtectedRoute>
                                      <RefactoredTimesheets />
                                    </ProtectedRoute>
                                  }
                                />

                                {/* Catch all route */}
                                <Route
                                  path="*"
                                  element={
                                    <ProtectedRoute>
                                      <AppRouteNotFound />
                                    </ProtectedRoute>
                                  }
                                />
                              </Routes>
                            </Suspense>
                          </ResourceOptimizationProvider>
                        </FinancialEngineProvider>
                      </TeamManagementProvider>
                    </ProjectProvider>
                  </DashboardScopeProvider>
                </AuthProvider>
              </ToastProvider>
            </SnackbarProvider>
          </LocalizationProvider>

          {/* PWA Install Prompt */}
          <Snackbar
            open={isInstallable && !isInstalled}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            sx={{ mb: 2 }}
          >
            <Alert
              severity="info"
              action={
                <>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={installPWA}
                    startIcon={<InstallIcon />}
                    sx={{ mr: 1 }}
                  >
                    Install
                  </Button>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={dismissInstallPrompt}
                    startIcon={<CloseIcon />}
                  >
                    Dismiss
                  </Button>
                </>
              }
            >
              Install UVEC PSA for a better mobile experience!
            </Alert>
          </Snackbar>

          {/* Offline Indicator */}
          {!isOnline && (
            <Snackbar
              open={!isOnline}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
              <Alert
                severity="warning"
                icon={<OfflineIcon />}
              >
                You're offline. Some features may be limited.
              </Alert>
            </Snackbar>
          )}

          {/* Install FAB for mobile */}
          {isInstallable && !isInstalled && (
            <>
              <Fab
                color="primary"
                aria-label="install app"
                sx={{
                  position: 'fixed',
                  bottom: 80,
                  right: 16,
                  display: { xs: 'flex', sm: 'none' }
                }}
                onClick={installPWA}
              >
                <InstallIcon />
              </Fab>
              <Fab
                color="default"
                size="small"
                aria-label="dismiss install prompt"
                sx={{
                  position: 'fixed',
                  bottom: 140,
                  right: 16,
                  display: { xs: 'flex', sm: 'none' }
                }}
                onClick={dismissInstallPrompt}
              >
                <CloseIcon />
              </Fab>
            </>
          )}
        </ThemeProvider>
      </AccessibilityProvider>
    </ErrorBoundary>
  );
}

export default App;
